<script setup lang="ts">
import { useHead } from '@unhead/vue';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';

useHead({
  title: 'Privacy Policy | LLM-SR',
  meta: [
    {
      name: 'description',
      content: 'LLM-SR privacy policy. We collect the minimum data needed to make the app work and we don\'t sell your data.',
    },
  ],
});
</script>

<template>
  <div class="min-h-screen bg-background flex flex-col">
    <AppHeader />

    <main class="flex-1 max-w-2xl mx-auto px-4 py-12">
      <h1 class="text-3xl font-bold text-foreground mb-8">Privacy Policy</h1>

      <div class="space-y-6 text-muted-foreground">
        <p class="text-sm">Last updated: February 2026</p>

        <section>
          <h2 class="text-xl font-semibold text-foreground mb-2">The short version</h2>
          <p>
            LLM-SR is a small project. We collect the minimum data needed to make the app work,
            we don't sell your data, and we don't do anything creepy with it.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-foreground mb-2">What we collect</h2>
          <ul class="list-disc list-inside space-y-1">
            <li><strong class="text-foreground">Email</strong> &mdash; required to create your account</li>
            <li><strong class="text-foreground">Name</strong> &mdash; optional, just for display</li>
            <li><strong class="text-foreground">Flashcard content</strong> &mdash; the questions and answers you create</li>
          </ul>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-foreground mb-2">How we use it</h2>
          <p>
            Your email is used to log you in. Your flashcards are stored so you can review them.
            That's it.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-foreground mb-2">Analytics &amp; error tracking</h2>
          <p>
            We use <a href="https://plausible.io" class="text-foreground underline hover:text-primary transition" target="_blank" rel="noopener">Plausible</a>
            for privacy-friendly analytics (no cookies, no personal data). We use
            <a href="https://sentry.io" class="text-foreground underline hover:text-primary transition" target="_blank" rel="noopener">Sentry</a>
            to catch bugs &mdash; it only collects error data to help us fix issues.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-foreground mb-2">Where your data lives</h2>
          <p>
            Your data is stored in a PostgreSQL database hosted on
            <a href="https://render.com" class="text-foreground underline hover:text-primary transition" target="_blank" rel="noopener">Render</a>
            (US-based).
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-foreground mb-2">Third parties</h2>
          <p>
            We don't sell or share your data with anyone. In the future, we may use
            <a href="https://stripe.com" class="text-foreground underline hover:text-primary transition" target="_blank" rel="noopener">Stripe</a>
            to process payments if we add paid features.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-foreground mb-2">Your rights</h2>
          <p>
            Want to export your data or delete your account? Just email us and we'll take care of it.
            We're working on making this self-serve.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-foreground mb-2">Contact</h2>
          <p>
            Questions? Reach out at
            <a href="mailto:mark.janzer@gmail.com" class="text-foreground underline hover:text-primary transition">mark.janzer@gmail.com</a>
          </p>
        </section>
      </div>
    </main>

    <AppFooter />
  </div>
</template>
